import { buildApiUrl } from "../../config.js";
import React, { useState, useEffect } from "react";
import { MdOutlineOpenInNew } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import TransferMoney from "./TransferMoney";
import { ColorRing } from "react-loader-spinner";
import convertToCr from "../ui/convertToCr";
import { FaPlus } from "react-icons/fa";

const PaymentTable = () => {
  const [payments, setPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isTransferModalOpen, setTransferModalOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);

  const navigate = useNavigate();
  const userRole = localStorage.getItem("role");

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        buildApiUrl("/api/orderItem/order/client/clientordersummary"),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      if (data?.clientDetails) {
        setPayments(data.clientDetails);
      } else {
        setError("Invalid response format. Expected clientDetails.");
      }
    } catch (error) {
      setError("An error occurred while fetching payment data.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredPayments = payments.filter(
    (payment) =>
      payment.clientId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.client?.username
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      payment.totalEstimateAmount?.toString().includes(searchTerm)
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPayments.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleNavigate = (paymentId) => {
    if (userRole === "finance") {
      navigate(`/finance/client/payment/${paymentId}`);
    } else {
      navigate(`/admin/client/payment/${paymentId}`);
    }
  };

  const openTransferModal = (clientId) => {
    setSelectedClientId(clientId);
    setTransferModalOpen(true);
  };

  const closeTransferModal = () => {
    setTransferModalOpen(false);
    setSelectedClientId(null);
  };

  return (
    <div className="bg-white">
      <div className="">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-2 gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between sm:space-x-4 w-full">
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Search Payments..."
              className="px-2 py-1 border border-gray-300 bg-gray-50 rounded-md shadow-sm w-full sm:w-1/3 focus:outline-none focus:ring-2 focus:ring-blue-700"
            />
            <button
              onClick={() => openTransferModal(null)}
              className="bg-indigo-600 flex gap-1 justify-center items-center text-white px-3 py-2 text-xs font-medium w-full sm:w-fit rounded-md shadow-lg hover:bg-indigo-800 mt-2 sm:mt-0"
            >
              <FaPlus size={12} />
              Transfer Money
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center">
            <ColorRing
              visible={true}
              height="80"
              width="80"
              ariaLabel="color-ring-loading"
              wrapperStyle={{}}
              wrapperClass="color-ring-wrapper"
              colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
            />
          </div>
        ) : error ? (
          <div className="text-center py-4 text-red-500">
            <p>{error}</p>
          </div>
        ) : (
          <div className="overflow-x-auto max-w-[100vw] rounded-md border shadow-md border-gray-300 max-h-[40rem]">
            <table className="min-w-full table-auto rounded-lg overflow-scroll shadow-md border-spacing-0 border-collapse">
              <thead className="bg-gray-700 text-white sticky top-0 z-10">
                <tr>
                  <th className="p-2 text-left font-normal tracking-wide">#</th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Client Name
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Contact Person
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Mobile No
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Total Estimate Amount
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Current Balance
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Paid Balance
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Total TPT Charges
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Payment Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {currentItems.map((payment, index) => (
                  <tr
                    key={payment.clientId}
                    className="hover:bg-gray-100 border divide-x text-gray-700 divide-gray-200 border-b border-collapse border-gray-200 transition-colors duration-200 cursor-pointer"
                  >
                    <td className="px-2 py-0 5">
                      {indexOfFirstItem + index + 1}
                    </td>
                    <td className="px-2 py-0.5 font-semibold">
                      {payment?.firmName}
                    </td>
                    <td className="px-2 py-0.5">{payment?.contactPerson}</td>
                    <td className="px-2 py-0.5">{payment?.mobileNo}</td>
                    <td className="px-2 py-0.5">
                      ₹{convertToCr(payment.totalEstimateAmount)}
                    </td>
                    <td className="px-2 py-0.5 font-semibold">
                      {payment.currentBalance < 0 ? (
                        <div className="flex items-baseline gap-2">
                          <span className="text-xs font-medium text-green-600">
                            ₹
                            {convertToCr(
                              Math.abs(payment.currentBalance).toFixed(2)
                            )}
                          </span>
                          <span className="text-[10px] font-medium text-white bg-green-500 px-1 rounded-full">
                            Adv.
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs font-medium text-red-600">
                          ₹{convertToCr(payment.currentBalance.toFixed(2))}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-0.5">
                      ₹{convertToCr(payment.paidBalance)}
                    </td>
                    <td className="px-2 py-0.5">
                      ₹{convertToCr(payment.totalTPTCharges)}
                    </td>
                    <td className="px-2 py-0.5 text-sm text-center">
                      <button
                        onClick={() => handleNavigate(payment.clientId)}
                        className="text-green-600 hover:text-green-700"
                      >
                        <MdOutlineOpenInNew size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex justify-between items-center">
          <span className="text-sm text-gray-500">
            Showing {indexOfFirstItem + 1} to{" "}
            {Math.min(indexOfLastItem, payments.length)} of {payments.length}{" "}
            payments
          </span>
          <div className="flex space-x-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-xs font-medium bg-gray-200 text-gray-600 rounded-lg"
            >
              Prev
            </button>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-xs font-medium bg-gray-200 text-gray-600 rounded-lg"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {isTransferModalOpen && (
        <TransferMoney
          clientId={selectedClientId}
          setLoading={setLoading}
          fetchPaymentSummary={fetchPayments}
          closeModal={closeTransferModal}
        />
      )}

      <ToastContainer />
    </div>
  );
};

export default React.memo(PaymentTable);
